import { atom } from "recoil";
import {
  recoilPersist as recoilPersistLocalStorage,
  recoilPersist as recoilPersistSessionStorage,
} from "recoil-persist";

import { Agent, Company, AgentProfileImage, CompanyLogoImage, MasterAgentSearchTag } from "../api/generated/src";
import { defaultPropertySearchFormSchema, PropertySearchFormSchema } from "../types/PropertySearch";
import { defaultUserSearchFormType, SearchFormType } from "../types/UsersSearch";

const { persistAtom: persistAtomLocalStorage } = recoilPersistLocalStorage({
  key: "recoil-persist-local", // ローカルストレージ用のキー
  storage: localStorage, // ローカルストレージを使用
});

const { persistAtom: persistAtomSessionStorage } = recoilPersistSessionStorage({
  key: "recoil-persist-session", // セッションストレージ用のキー
  storage: sessionStorage, // セッションストレージを使用
});

export type LoggedIn = {
  logged_in: boolean;
};
export const LoggedInState = atom<LoggedIn>({
  key: "LoggedInState",
  default: {
    logged_in: false,
  },
  // タブ分離のためセッションストレージを使用
  effects_UNSTABLE: [persistAtomSessionStorage],
});

export const AgentState = atom<Agent>({
  key: "AgentState",
  default: {
    id: 0,
    profile: "",
    first_name: "",
    family_name: "unknown",
    is_enabled_notification_new_user: true,
    is_enabled_notification_new_property: true,
    agent_profile_image: {
      image_url: "/user.svg",
    } as AgentProfileImage,
  } as Agent,
  // タブ分離のためセッションストレージを使用
  effects_UNSTABLE: [persistAtomSessionStorage],
});

type CompanyAchievement = {
  achievement_name?: string;
  achievement_area?: string;
  achievement_price?: number;
  file_url?: string | null;
  object_storage_path?: string | null;
};

type CompanyLimitedProperty = {
  property_name?: string;
  property_area?: string;
  property_price?: number;
  property_monthly_rent?: number;
  property_nearest_station?: string;
  property_building_age?: number;
  file_url?: string | null;
  object_storage_path?: string | null;
};

export type CompanyStateType = Company & {
  sales_achievements?: CompanyAchievement[];
  limited_properties?: CompanyLimitedProperty[];
};

export const CompanyState = atom<CompanyStateType>({
  key: "CompanyState",
  default: {
    id: 0,
    name: "未入力",
    home_page_url: "",
    short_appeal_message: "",
    achievements: "",
    strengths_of_properties_for_sale_and_purchase: "",
    strengths_of_partner_financial_institutions: "",
    strengths_of_properties_for_management: "",
    appeal_point: "",
    company_agent_search_tag_ids: [],
    company_logo_image: {
      image_url: "/user.svg",
    } as CompanyLogoImage,
    sales_achievements: [],
    limited_properties: [],
    created_at: new Date(),
    updated_at: new Date(),
  } as CompanyStateType,
  // タブ分離のためセッションストレージを使用
  effects_UNSTABLE: [persistAtomSessionStorage],
});

export const SearchFormTypeForUserListState = atom<SearchFormType>({
  key: "SearchFormTypeForUserListState",
  default: {
    ...defaultUserSearchFormType,
  },
  // 検索条件保存はブラウザ閉じたらリセットしたい要件のためセッションストレージに保存
  effects_UNSTABLE: [persistAtomSessionStorage],
});

export const SearchFormTypeForProposedUserListState = atom<SearchFormType>({
  key: "SearchFormTypeForProposedUserListState",
  default: {
    ...defaultUserSearchFormType,
  },
  // 検索条件保存はブラウザ閉じたらリセットしたい要件のためセッションストレージに保存
  effects_UNSTABLE: [persistAtomSessionStorage],
});

export const SearchFormSchemaForPropertyListState = atom<PropertySearchFormSchema>({
  key: "SearchFormSchemaForPropertyListState",
  default: {
    ...defaultPropertySearchFormSchema,
  },
  // 検索条件保存はブラウザ閉じたらリセットしたい要件のためセッションストレージに保存
  effects_UNSTABLE: [persistAtomSessionStorage],
});

export const MasterAgentSearchTagsState = atom<MasterAgentSearchTag[]>({
  key: "MasterAgentSearchTagsState",
  default: [],
});

export type IdNameMaster = {
  id?: number;
  name?: string;
};

export const MasterInterestedPropertyTypesState = atom<IdNameMaster[]>({
  key: "MasterInterestedPropertyTypesState",
  default: [],
});

export type AdminVisibilitySettings = {
  showUserScore: boolean;
  showPropertyScore: boolean;
};

export const AdminVisibilitySettingsState = atom<AdminVisibilitySettings>({
  key: "AdminVisibilitySettingsState",
  default: {
    showUserScore: true,
    showPropertyScore: true,
  },
  effects_UNSTABLE: [persistAtomLocalStorage],
});

export type ImpersonationStateType = {
  // admin からエージェントへ代理ログインしている状態か
  active: boolean;
  // サーバーへの問い合わせが済んだか（済むまで代理ログイン限定の導線を出さない）
  loaded: boolean;
  // 掲載終了後のプランナーを参照専用で開いている状態か（API が GET / HEAD 以外を拒否する）
  readOnly: boolean;
};

// 代理ログイン中かどうか。AIオファーの導線の出し分けに使う。
// sessionStorage には永続化しない。access_token が期限切れでも残っている状態があり得るため、
// 表示はサーバーの答えだけを正とする。
export const ImpersonationState = atom<ImpersonationStateType>({
  key: "ImpersonationState",
  default: {
    active: false,
    loaded: false,
    readOnly: false,
  },
});

